import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { useGetPdfRestrictionsQuery } from '@/pages/shared/shared.query';
import './pdf-restrictions.less';

const PdfRestrictionContext = createContext(null);

// Visible controls register their IDs. One debounced RTK request checks the page,
// rather than issuing a separate network request for every PDF button.
const VisiblePdfRestrictions = ({ children }) => {
    const registrations = useRef(new Map());
    const timer = useRef();
    const [ids, setIds] = useState([]);
    const register = useCallback((id) => {
        if (!Number.isInteger(id) || id <= 0) return () => {};
        const update = () => {
            clearTimeout(timer.current);
            timer.current = setTimeout(() => {
                setIds([...registrations.current.keys()].sort((a, b) => a - b));
            }, 80);
        };
        registrations.current.set(id, (registrations.current.get(id) || 0) + 1);
        update();
        return () => {
            const count = registrations.current.get(id) || 0;
            if (count <= 1) registrations.current.delete(id);
            else registrations.current.set(id, count - 1);
            update();
        };
    }, []);
    useEffect(() => () => clearTimeout(timer.current), []);
    const { currentData, isFetching, isError } = useGetPdfRestrictionsQuery(ids, { skip: !ids.length });
    const value = useMemo(() => ({
        register, isFetching, isError,
        byId: new Map((currentData || []).map(item => [item.documentFileId, item])),
    }), [register, currentData, isFetching, isError]);
    return <PdfRestrictionContext.Provider value={value}>{children}</PdfRestrictionContext.Provider>;
};

export const PdfRestrictionsProvider = ({ children }) => {
    const loggedIn = useSelector(state => Boolean(state.userDetails?.isLogin));
    return loggedIn ? <VisiblePdfRestrictions>{children}</VisiblePdfRestrictions> : children;
};

export const usePdfRestriction = (documentFileId) => {
    const context = useContext(PdfRestrictionContext);
    const register = context?.register;
    useEffect(() => register?.(documentFileId), [register, documentFileId]);
    return context?.byId.get(documentFileId);
};

export const PdfRestrictionBadge = ({ documentFileId }) => {
    const restriction = usePdfRestriction(documentFileId);
    if (!restriction?.isRestricted) return null;
    const help = [restriction.notice, restriction.downloadBlockedReason].filter(Boolean).join(' ');
    return (
        <Tooltip title={help} trigger={['hover', 'focus']}>
            <span className="dms-pdf-restriction" tabIndex={0} aria-label={`Restricted PDF. ${help}`}>
                <i className="ri-lock-line" aria-hidden="true" /> Restricted PDF
            </span>
        </Tooltip>
    );
};

export const PdfFormatLabel = ({ documentFileId, passwordRequired, label = 'PDF' }) => {
    const restriction = usePdfRestriction(documentFileId);
    if (restriction?.isRestricted && !passwordRequired) {
        return <PdfRestrictionBadge documentFileId={documentFileId} />;
    }
    return <>
        {passwordRequired ? <i className="lock-icon ri-lock-line" />
            : <img src="/images/pdf-line.png" alt="" className="pdf-icon" />}
        {React.isValidElement(label) ? label : <span>{label}</span>}
    </>;
};
